import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription, forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { UrlDetailComponent } from '../url-detail/url-detail';
import { ImpactBriefService, type ImpactBriefPayload } from '../../services/impact-brief.service';
import { PlanV2IssueQueueService } from '../../services/plan-v2-issue-queue.service';
import { NotificationService } from '../../services/notification.service';
import { ReviewSelectionService } from '../../services/review-selection.service';
import { reviewSendLabel, type ReviewSelectableItem } from '../../utils/review-selection';
import {
  buildReviewQueue,
  claimedGap,
  newlyClaimedUrlIds,
  nextReviewIndex,
  reviewOutcome,
  reviewProgress,
  type ReviewQueueEntry
} from '../../utils/review-queue';

/** A dismissal that can be taken back. */
export interface ReviewDismissUndo {
  kind: 'dismiss';
  urlId: string;
  url: string;
  path: string;
  index: number;
}

/**
 * Work items that can be taken back — plural, because one click on an
 * internal-link card writes one per selected link and they are a single action.
 */
export interface ReviewWorkItemUndo {
  kind: 'work_item';
  urlId: string;
  path: string;
  issueKeys: string[];
  index: number;
}

/**
 * A whole page's triage, taken back in one go.
 *
 * The unit follows the decision. Once everything on a page is sent with one
 * keystroke, undoing one item of it would answer a question nobody asked —
 * what the operator regrets is the press.
 */
export interface ReviewSendUndo {
  kind: 'send';
  urlId: string;
  path: string;
  items: ReviewSelectableItem[];
  index: number;
}

export type ReviewUndo = ReviewDismissUndo | ReviewWorkItemUndo | ReviewSendUndo;

/**
 * Review mode — the Impact Brief worked one URL at a time.
 *
 * The packages strip answers "where is the damage?". This answers "what am I
 * doing about it?", and they are different enough jobs to want different
 * screens. The strip is a table you scan; this is a belt you clear, and the
 * measure of it is how few motions a decision costs: look, press, next.
 *
 * It replaced a drawer, and the reasons are worth keeping written down. The
 * drawer was 80vw of fixed panel over a 1100px centred column, so on a wide
 * display it covered the queue it opened from *and* overshot it on both sides —
 * the operator lost their place on every page they reviewed. Widening or
 * narrowing it only trades which of those two problems you have. The real cost
 * was never the pixels: it was that a scrim-backed panel makes the list behind
 * it dead, so every URL cost a close, a scroll, a hunt for the next row and a
 * re-open. Forty-five pages is that tax forty-five times.
 *
 * So: no overlay, no scrim, no list to lose your place in. The review IS the
 * screen, the keyboard moves the belt, and the only thing that persists between
 * items is the progress rail and the running total.
 */
@Component({
  selector: 'app-review-mode',
  standalone: true,
  imports: [CommonModule, RouterModule, UrlDetailComponent],
  templateUrl: './review-mode.html',
  styleUrl: './review-mode.css'
})
export class ReviewModeComponent implements OnInit, OnDestroy {
  loading = true;
  /** Set only when the brief could not be read at all. */
  error: string | null = null;

  /** The domain the belt was composed against. Pinned, never re-resolved. */
  domain = '';
  brief: ImpactBriefPayload | null = null;
  queue: ReviewQueueEntry[] = [];
  index = 0;

  /**
   * The end of the belt is a state, not an index.
   *
   * Deciding the last item must land somewhere that says so. Clamping to the
   * final index instead would leave the operator on a page they have already
   * answered, with no signal that there is nothing after it.
   */
  finished = false;

  /** Same filter the drawer carried, and it stays set across items on purpose. */
  issuesOnly = true;

  readonly dismissedUrlIds = new Set<string>();
  readonly claimedUrlIds = new Set<string>();
  readonly seenUrlIds = new Set<string>();

  /**
   * URLs that already had a work item when the loop started.
   *
   * The running total counts what THIS session claimed. Without a baseline it
   * would open at whatever the domain had accumulated over previous weeks and
   * count that as the operator's work, which is both wrong and — because the
   * number would start large and barely move — unsatisfying in exactly the way
   * the total exists to avoid.
   */
  private baselineClaimedUrlIds = new Set<string>();

  /** Work items already written, so a new one can be told from the rest. */
  private lastWorkItemKeys = new Set<string>();

  /**
   * The last decision that can still be taken back, whichever kind it was.
   *
   * Both kinds, because `u` was wired only to dismissals at first and that was
   * simply wrong: the bar says "Undo" and the hint says `u undo`, so the
   * promise on screen is the last thing you did, not the last thing of one
   * particular sort. Sending a page to work items is the bigger commitment of
   * the two — it is the one that most needs to be cheap to make.
   *
   * One deep rather than a stack. Undo here is not a history feature, it is
   * what makes a decision cheap enough to make quickly — it needs to cover the
   * press you just regretted, and an operator who wants to revisit four pages
   * back is navigating, not undoing, and can press `k` four times.
   */
  undoable: ReviewUndo | null = null;

  /**
   * URLs with a write in flight, so the same decision is never sent twice.
   *
   * A set keyed by URL rather than one boolean for the screen, and the
   * difference is not academic: a single flag is a latch over the whole loop,
   * and a request that never settles — not one that fails, one that hangs —
   * leaves it down forever. `finalize` does not save you there either, because
   * an observable that never terminates never runs it. What that costs is the
   * two keys the entire loop is made of, dead, on a screen that still looks
   * interactive.
   *
   * Scoped per URL, a hung write can only ever block the one page it belongs
   * to, and that page has already been decided and advanced past.
   */
  private readonly saving = new Set<string>();

  /** Whether the page on screen right now has a write in flight. */
  get busy(): boolean {
    const entry = this.current;
    return entry ? this.saving.has(entry.urlId) : false;
  }

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly impactBriefService: ImpactBriefService,
    private readonly issueQueue: PlanV2IssueQueueService,
    private readonly notifications: NotificationService,
    private readonly selection: ReviewSelectionService
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.load(params.get('domain'), params.get('url'));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.clearPrefetchTimer();
    this.clearSpinnerTimer();
  }

  // ── Loading ──────────────────────────────────────────────────────────────

  /**
   * One brief read, uncapped.
   *
   * The composer caps each package at five pages, which is right for a strip
   * and wrong for a belt: "Review 45 pages" that walks you through twenty is a
   * lie the operator only catches at the end.
   *
   * This read the brief TWICE at first — once to learn the package ids, once to
   * ask for them uncapped — because `expand` took names and the names are only
   * knowable from a brief. That doubled the slowest call in the app on the one
   * screen you reach by waiting for it, so the route grew `expand=all` and the
   * second read went away.
   */
  private load(domainParam: string | null, startUrl: string | null): void {
    this.loading = true;
    this.error = null;

    const pinned = (domainParam || '').trim() || null;
    this.impactBriefService.getBrief(pinned, { expandAll: true }).subscribe((payload) => {
      if (!payload) {
        this.error = 'Could not load this period’s brief.';
        this.loading = false;
        return;
      }

      this.brief = payload;
      this.domain = (payload.domain || pinned || '').trim();
      this.loadDispositions(startUrl);
    });
  }

  /**
   * The queue is only knowable after the dispositions are.
   *
   * A page already dealt with in an earlier session — put aside, or worked
   * until every card was gone — is not a decision still owed, so it never
   * enters the belt, and that filter needs the records loaded. Building first
   * and filtering later would flash items that immediately vanish.
   *
   * The skip list is read ONCE, on entry. A page decided during this pass stays
   * where it is until the operator leaves and comes back: a belt that pulled
   * items out from under the person working it would renumber "12 of 45" mid
   * decision and move the next page under their cursor.
   */
  private loadDispositions(startUrl: string | null): void {
    const finish = () => {
      this.buildQueue(startUrl);
      this.watchClaims();
      this.loading = false;
    };

    if (!this.domain) {
      finish();
      return;
    }

    this.issueQueue.load(this.domain).subscribe({
      next: () => finish(),
      // A queue that will not load costs the loop its skip-list and its running
      // total, not its purpose. The pages are still reviewable.
      error: () => finish()
    });
  }

  private buildQueue(startUrl: string | null): void {
    this.queue = buildReviewQueue(this.brief?.packages ?? [], {
      // Both lists, spelled out. `decidedUrlIds()` already contains everything
      // `ignoredUrlIds()` does — Put aside is a page-scoped disposition — but
      // the belt's skip rule is "pages already dealt with, however they were
      // dealt with", and a call site that says so cannot be broken by a later
      // change to what counts as a decision.
      skipUrlIds: [...this.issueQueue.ignoredUrlIds(), ...this.issueQueue.decidedUrlIds()]
    });
    this.baselineClaimedUrlIds = this.workItemUrlIds();

    const wanted = (startUrl || '').trim();
    const at = wanted ? this.queue.findIndex((entry) => entry.url === wanted) : -1;
    this.index = at >= 0 ? at : 0;
    this.finished = this.queue.length === 0;
    // The first page waits like any other, so it arms the same spinner.
    if (!this.finished) this.armSpinner();
    this.markSeen();
  }

  /**
   * The running total, read off the queue service rather than off clicks.
   *
   * The Work item buttons live inside `app-url-detail`, several components
   * below this one, and one visit can produce several work items. Watching what
   * was actually persisted means the total cannot drift from the truth, and no
   * output has to be threaded up through the embed to keep it honest.
   */
  private watchClaims(): void {
    this.lastWorkItemKeys = this.workItemKeys();
    this.subscriptions.add(
      this.issueQueue.records$.subscribe(() => {
        const keys = this.workItemKeys();
        const added = [...keys].filter((key) => !this.lastWorkItemKeys.has(key));
        this.lastWorkItemKeys = keys;

        // Recomputed from the records every time rather than accumulated. The
        // set is derived state, and a work item can leave as well as arrive —
        // through undo here, or through the card's own "Back to issues" — so a
        // total that only ever counts up would keep claiming demand the queue
        // no longer has any work against.
        const claimed = newlyClaimedUrlIds(this.baselineClaimedUrlIds, this.workItemUrlIds());
        this.claimedUrlIds.clear();
        for (const urlId of claimed) this.claimedUrlIds.add(urlId);

        if (added.length) this.offerWorkItemUndo(added);
      })
    );
  }

  private workItemKeys(): Set<string> {
    const keys = new Set<string>();
    for (const item of this.issueQueue.workItems()) {
      const key = (item.issueKey || '').trim();
      if (key) keys.add(key);
    }
    return keys;
  }

  /**
   * "Sent to work items" — offered back, keyed by the page it happened on.
   *
   * Grouping by page is what makes a batch behave. One click on an internal-link
   * card writes one work item per selected link, and each lands as its own
   * record emission; keeping the offer keyed to the URL lets the second, third
   * and fourth join the offer the first one opened instead of replacing it. So
   * undo takes back the action the operator performed rather than the last
   * write it happened to produce — and it does that without a coalescing timer,
   * which is the kind of thing that made undo feel broken the first time round.
   */
  private offerWorkItemUndo(addedKeys: string[]): void {
    const byKey = new Map(this.issueQueue.workItems().map((item) => [item.issueKey, item]));
    const urlId = (byKey.get(addedKeys[0])?.urlId || '').trim();
    if (!urlId) return;

    // A send writes work items too, and re-reads the queue afterwards — so its
    // own records arrive here as "newly claimed" and would replace the send's
    // undo offer with a work-item one. That is not a smaller version of the
    // same offer: taking back work items leaves the page's put-aside decisions
    // written and does not re-tick anything, so `u` would silently do half the
    // job the operator asked for.
    //
    // The standing send offer wins for the page it belongs to. The cost is an
    // edge case worth naming: going back to a page you just sent and creating
    // one work item by hand leaves the send offer standing rather than
    // offering that item back. The send is the larger and likelier regret.
    const standing = this.undoable;
    if (standing && standing.kind === 'send' && standing.urlId === urlId) return;

    const entry = this.queue.find((e) => e.urlId === urlId);
    const path = entry?.path || (byKey.get(addedKeys[0])?.url ?? '');
    const mine = addedKeys.filter((key) => (byKey.get(key)?.urlId || '').trim() === urlId);

    if (standing && standing.kind === 'work_item' && standing.urlId === urlId) {
      this.undoable = {
        ...standing,
        issueKeys: [...new Set([...standing.issueKeys, ...mine])]
      };
      return;
    }

    this.undoable = {
      kind: 'work_item',
      urlId,
      path,
      issueKeys: mine,
      index: entry ? this.queue.indexOf(entry) : this.index
    };
  }

  private workItemUrlIds(): Set<string> {
    const ids = new Set<string>();
    for (const item of this.issueQueue.workItems()) {
      const urlId = (item.urlId || '').trim();
      if (urlId) ids.add(urlId);
    }
    return ids;
  }

  // ── The belt ─────────────────────────────────────────────────────────────

  get current(): ReviewQueueEntry | null {
    if (this.finished || !this.queue.length) return null;
    return this.queue[Math.min(this.index, this.queue.length - 1)] ?? null;
  }

  /**
   * The item after this one, mounted hidden so it is already loaded when the
   * operator gets to it.
   *
   * Measured on the pilot: both snapshots are ready at ~3ms, and the remaining
   * ~4.9s is one live marked-competitor evaluation — the SERP-ranked rival is
   * not in the precomputed set (see TECHNICAL_DEBT 0ae). Until that build
   * covers it, the wait is real, and the cheapest way to stop an operator
   * paying it forty-five times is to spend it while they are reading the page
   * before.
   *
   * Only ever ONE ahead. Two would double the load on a sidecar that is
   * already the bottleneck and is effectively single-threaded (0ad), and the
   * operator can only read one page at a time anyway.
   *
   * Null while the current page is still settling, so the prefetch never
   * competes with the page being waited on — it starts once the visible card
   * is done, which is exactly when the operator starts reading.
   */
  get prefetch(): ReviewQueueEntry | null {
    if (this.finished || !this.queue.length) return null;
    if (!this.currentSettled || !this.settledLongEnoughToPrefetch) return null;
    return this.queue[this.index + 1] ?? null;
  }

  /**
   * Whether the operator has stayed put long enough for a prefetch to be worth
   * starting.
   *
   * A prefetch only pays if they linger. Someone stepping quickly through the
   * belt advances before it resolves, and then it is not a head start — it is
   * an extra evaluation queued against a sidecar that runs one at a time, in
   * front of the page they are actually waiting for. Reported as pages taking
   * "over 2-3 minutes", and made worse by this feature rather than better.
   *
   * Two seconds is well under the time it takes to read a card and well over
   * the time it takes to press `j` again.
   */
  /**
   * Show one spinner for the whole page rather than letting it assemble.
   *
   * The card-level placeholder still let the page build itself in pieces: the
   * links block would land, then supporting pages, and the screen moved under
   * the reader each time. A single spinner and then the finished page is less
   * jarring than watching it stack up.
   *
   * Same 324ms threshold as everything else here, so a warm page — which is
   * most of them — goes straight to content and never shows a spinner at all.
   */
  private static readonly SPINNER_AFTER_MS = 324;
  private spinnerVisible = false;
  private spinnerTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * When the spinner starts explaining itself.
   *
   * Measured 2026-09-03: a page whose rival has not been compared before takes
   * 14–18s of sidecar time on its own, and that work is shared by every page
   * the operator has opened and moved on from — the comparison service takes
   * two at a time and the rest queue. A spinner with no upper bound made a
   * two-minute queue and a stuck request look identical, and "it's been
   * hanging 2–3 minutes" was reported as the app being broken.
   *
   * 15s is just under a single cold comparison, so a page that is merely cold
   * gets the line as its own wait ends, and a page that is genuinely queued
   * gets it while it still matters. One line, same spinner: not a second
   * loading state.
   */
  static readonly SLOW_AFTER_MS = 15_000;
  static readonly SLOW_LOADING_MESSAGE =
    'Still comparing — a first look at a page takes a while, and it waits behind any other pages you opened.';
  private spinnerSlow = false;
  private slowTimer: ReturnType<typeof setTimeout> | null = null;

  get showLoading(): boolean {
    return !this.currentSettled && this.spinnerVisible;
  }

  /** The one-line explanation under the spinner once the wait is long. */
  get slowLoadingMessage(): string | null {
    return this.showLoading && this.spinnerSlow ? ReviewModeComponent.SLOW_LOADING_MESSAGE : null;
  }

  /**
   * The page renders when it is ready, and not before.
   *
   * This used to also return true inside the quiet window, which produced the
   * worst of both: a half-built page for 324ms, then a spinner replacing it,
   * then the real page. Three states where there should be two.
   *
   * So the window renders NOTHING — no content, no spinner. A warm page is
   * blank for the two milliseconds it takes to settle and then appears whole;
   * a slow one is blank briefly, then shows a spinner until it is ready. What
   * never happens is content arriving in pieces.
   */
  get showContent(): boolean {
    return this.currentSettled;
  }

  private static readonly PREFETCH_AFTER_MS = 2000;
  private settledLongEnoughToPrefetch = false;
  private prefetchTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Whether the visible page has finished loading.
   *
   * Read off the DOM's own signal rather than tracked here: the embed owns its
   * loading, and duplicating that state in the host is how the two drift.
   */
  currentSettled = false;

  onCurrentSettledChange(settled: boolean): void {
    // Guarded because this fires from a child's change detection; assigning
    // unconditionally would loop.
    if (this.currentSettled === settled) return;
    this.currentSettled = settled;
    this.clearPrefetchTimer();
    this.settledLongEnoughToPrefetch = false;
    if (settled) {
      this.clearSpinnerTimer();
      this.spinnerVisible = false;
    } else {
      this.armSpinner();
    }
    if (!settled) return;
    this.prefetchTimer = setTimeout(() => {
      this.prefetchTimer = null;
      this.settledLongEnoughToPrefetch = true;
    }, ReviewModeComponent.PREFETCH_AFTER_MS);
  }

  private armSpinner(): void {
    this.clearSpinnerTimer();
    this.spinnerVisible = false;
    this.spinnerTimer = setTimeout(() => {
      this.spinnerTimer = null;
      this.spinnerVisible = true;
    }, ReviewModeComponent.SPINNER_AFTER_MS);
    // Counted from the same moment as the spinner, so "15s" means 15s of
    // waiting on this page — not 15s after the spinner happened to appear.
    this.slowTimer = setTimeout(() => {
      this.slowTimer = null;
      this.spinnerSlow = true;
    }, ReviewModeComponent.SLOW_AFTER_MS);
  }

  private clearSpinnerTimer(): void {
    this.spinnerSlow = false;
    if (this.slowTimer) {
      clearTimeout(this.slowTimer);
      this.slowTimer = null;
    }
    if (!this.spinnerTimer) return;
    clearTimeout(this.spinnerTimer);
    this.spinnerTimer = null;
  }

  private clearPrefetchTimer(): void {
    if (!this.prefetchTimer) return;
    clearTimeout(this.prefetchTimer);
    this.prefetchTimer = null;
  }

  get progress(): { position: number; total: number; percent: number } {
    return reviewProgress(this.queue.length, this.index);
  }

  get outcome(): { dismissed: number; claimed: number; seen: number; decided: number } {
    return reviewOutcome(this.dismissedUrlIds, this.claimedUrlIds, this.seenUrlIds);
  }

  /** Uncaptured demand this session put into work items. */
  get claimed(): number {
    return claimedGap(this.queue, this.claimedUrlIds);
  }

  get hasClaimed(): boolean {
    return this.claimedUrlIds.size > 0;
  }

  /** True on the item the operator has already decided — the rail says so. */
  isDecided(entry: ReviewQueueEntry | null): boolean {
    if (!entry) return false;
    return this.dismissedUrlIds.has(entry.urlId) || this.claimedUrlIds.has(entry.urlId);
  }

  private markSeen(): void {
    const entry = this.current;
    if (entry) this.seenUrlIds.add(entry.urlId);
  }

  /**
   * Forward, and off the end deliberately.
   *
   * Stepping past the last item finishes the loop rather than sticking. See
   * `finished` — the end has to be reachable or it never announces itself.
   */
  next(): void {
    if (!this.queue.length) return;
    if (this.index >= this.queue.length - 1) {
      this.finished = true;
      return;
    }
    this.index = nextReviewIndex(this.queue.length, this.index, 1);
    this.currentSettled = false;
    this.clearPrefetchTimer();
    this.settledLongEnoughToPrefetch = false;
    this.armSpinner();
    this.markSeen();
  }

  previous(): void {
    if (!this.queue.length) return;
    // Backwards out of the summary lands on the last item, which is the one the
    // operator was looking at when they finished.
    if (this.finished) {
      this.finished = false;
      this.index = this.queue.length - 1;
      this.currentSettled = false;
      this.markSeen();
      return;
    }
    this.index = nextReviewIndex(this.queue.length, this.index, -1);
    this.currentSettled = false;
    this.clearPrefetchTimer();
    this.settledLongEnoughToPrefetch = false;
    this.armSpinner();
    this.markSeen();
  }

  // ── Decisions ────────────────────────────────────────────────────────────

  /**
   * "Not this one" — the whole URL, out of this period's brief.
   *
   * `url_ignored` is the existing whole-page disposition the Issues queue
   * already uses, not a new concept invented for this screen: dismissing here
   * and dismissing there mean the same thing and are the same record, so a page
   * put aside in the loop stays put aside everywhere.
   *
   * The advance happens immediately, before the PATCH resolves. The decision is
   * already made and waiting on a round trip to move is the difference between
   * a loop that feels like a loop and one that feels like a form; the undo bar
   * is what makes that safe, and a failure puts the item back.
   */
  dismiss(): void {
    const entry = this.current;
    if (!entry || this.saving.has(entry.urlId) || !this.domain) return;
    this.saving.add(entry.urlId);

    const at = this.index;
    this.dismissedUrlIds.add(entry.urlId);
    this.offerUndo(entry, at);
    this.next();

    this.issueQueue
      .ignoreUrl(this.domain, entry.urlId, entry.url)
      .pipe(finalize(() => this.saving.delete(entry.urlId)))
      .subscribe({
        next: () => {},
        error: () => {
          this.dismissedUrlIds.delete(entry.urlId);
          this.clearUndo();
          this.index = at;
          this.finished = false;
          this.notifications.error('Could not dismiss that page. It is still in the queue.');
        }
      });
  }

  /**
   * Take back the last decision and go back to the page it was about.
   *
   * Returning to the item rather than only un-writing the record: an operator
   * pressing undo is saying "I want to look at that again", and leaving them
   * three pages further on with a restored record they cannot see would answer
   * a question they did not ask.
   */
  undo(): void {
    const pending = this.undoable;
    if (!pending || !this.domain) return;
    this.clearUndo();
    this.returnTo(pending.index);

    if (pending.kind === 'dismiss') {
      this.undoDismissal(pending);
      return;
    }
    if (pending.kind === 'send') {
      this.undoSend(pending);
      return;
    }
    this.undoWorkItems(pending);
  }

  private returnTo(index: number): void {
    this.finished = false;
    this.index = Math.min(Math.max(0, index), Math.max(0, this.queue.length - 1));
    this.markSeen();
  }

  private undoDismissal(pending: ReviewDismissUndo): void {
    this.dismissedUrlIds.delete(pending.urlId);
    this.saving.delete(pending.urlId);

    this.issueQueue.restoreIgnoredUrl(this.domain, pending.urlId).subscribe({
      next: () => {},
      error: () => {
        this.dismissedUrlIds.add(pending.urlId);
        this.notifications.error('Could not restore that page.');
      }
    });
  }

  /**
   * Back to the Issues queue, which is where they came from.
   *
   * `'issues'` is the same disposition the work-item card's own "Back to
   * issues" writes, so undoing here and undoing there leave the queue in one
   * state rather than two. The claimed total needs no adjustment: it is
   * recomputed from the records, and these records are about to stop being
   * work items.
   */
  private undoWorkItems(pending: ReviewWorkItemUndo): void {
    forkJoin(
      pending.issueKeys.map((key) => this.issueQueue.setDisposition(this.domain, key, 'issues'))
    ).subscribe({
      next: () => {},
      error: () => {
        this.notifications.error(
          pending.issueKeys.length === 1
            ? 'Could not take that work item back.'
            : 'Could not take those work items back.'
        );
      }
    });
  }

  /** What the undo bar says. The two decisions must not read alike. */
  undoLabel(pending: ReviewUndo): string {
    if (pending.kind === 'dismiss') return `Put aside ${pending.path}`;
    if (pending.kind === 'send') {
      const n = pending.items.length;
      return n === 1 ? `Sent 1 item on ${pending.path}` : `Sent ${n} items on ${pending.path}`;
    }
    const n = pending.issueKeys.length;
    return n === 1
      ? `Sent 1 work item on ${pending.path}`
      : `Sent ${n} work items on ${pending.path}`;
  }

  /**
   * The offer stands until it is used or replaced. No timer.
   *
   * It had one — eight seconds — and testing it on a live page is what showed
   * how wrong that is. The whole point of the item on screen is that it takes
   * a while to read; an operator who puts a page aside, reads two paragraphs of
   * the next one and then thinks better of it is exactly the person undo is
   * for, and a countdown takes the affordance away precisely while they are
   * still deciding. Persisting costs one line in a bar that is always there,
   * and the line names the page, so pressing `u` is never a blind jump.
   */
  private offerUndo(entry: ReviewQueueEntry, at: number): void {
    this.undoable = {
      kind: 'dismiss',
      urlId: entry.urlId,
      url: entry.url,
      path: entry.path,
      index: at
    };
  }

  clearUndo(): void {
    this.undoable = null;
  }

  // ── Send ─────────────────────────────────────────────────────────────────

  /** Everything on this page that a card has registered, ticked or not. */
  get sendSplit(): { send: ReviewSelectableItem[]; putAside: ReviewSelectableItem[] } {
    const entry = this.current;
    return entry ? this.selection.split(entry.urlId) : { send: [], putAside: [] };
  }

  get hasSendableItems(): boolean {
    const split = this.sendSplit;
    return split.send.length + split.putAside.length > 0;
  }

  get sendLabel(): string {
    if (!this.currentSettled) return 'Waiting for this page…';
    const split = this.sendSplit;
    return reviewSendLabel(split.send.length, split.putAside.length);
  }

  /**
   * A page whose findings are still arriving cannot be sent.
   *
   * This is the part that actually mattered. Measured on the pilot: while the
   * supporting-pages card was still evaluating, the button read "Send 2 items"
   * and would have sent two — silently omitting `topic_ownership`, the finding
   * that had not registered yet. The operator would have believed they queued
   * the whole page.
   *
   * A wrong count on a button is not a cosmetic problem when pressing it is
   * the decision. So the count is not shown until it is final, and the press
   * does nothing until then.
   */
  get sendBlocked(): boolean {
    return !this.currentSettled;
  }

  /**
   * One press, one request, one thing to undo.
   *
   * Advancing happens immediately, before the write resolves, for the same
   * reason dismissing does: the decision is already made and waiting on a round
   * trip is the difference between a loop and a form. The undo offer is what
   * makes that safe, and a failure puts the operator back on the page.
   */
  send(): void {
    const entry = this.current;
    if (!entry || !this.domain || this.saving.has(entry.urlId)) return;
    if (this.sendBlocked) return;
    const split = this.sendSplit;
    if (!split.send.length && !split.putAside.length) return;

    const at = this.index;
    const touched = [...split.send, ...split.putAside];
    this.saving.add(entry.urlId);
    this.offerSendUndo(entry, touched, at);
    this.next();

    this.selection
      .send(this.domain, entry.urlId)
      .pipe(finalize(() => this.saving.delete(entry.urlId)))
      .subscribe({
        next: () => {},
        error: () => {
          this.clearUndo();
          this.index = at;
          this.finished = false;
          this.notifications.error('Could not send that page. Nothing was queued.');
        }
      });
  }

  private offerSendUndo(entry: ReviewQueueEntry, items: ReviewSelectableItem[], at: number): void {
    this.undoable = {
      kind: 'send',
      urlId: entry.urlId,
      path: entry.path,
      items,
      index: at
    };
  }

  /**
   * Put the whole send back, and re-tick what was unticked.
   *
   * Re-ticking matters: an operator returning to a page they just sent should
   * find it as they found it the first time, not carrying the exceptions of a
   * decision they have just undone.
   */
  private undoSend(pending: ReviewSendUndo): void {
    this.selection.reselectAll(pending.items);
    this.selection.undo(this.domain, pending.items).subscribe({
      next: () => {},
      error: () => {
        this.notifications.error('Could not take that page back.');
      }
    });
  }

  toggleIssuesOnly(): void {
    this.issuesOnly = !this.issuesOnly;
  }

  exit(): void {
    this.router.navigate(['/dashboard']);
  }

  /**
   * The one way out of a stripped card, and the reason stripping it is safe.
   *
   * Everything this surface removes — the methodology, your own site's column,
   * the covered rows, the compare controls, the re-fetch buttons — is still on
   * URL Detail, unchanged. Making that one keystroke away is what turns "we hid
   * things from you" into "we did not make you read them". Exactly one hatch,
   * because two would be a menu and a menu is a decision.
   *
   * A new tab rather than a navigation: the belt is a place you are part-way
   * through, and losing your position to look something up would cost more than
   * the lookup is worth.
   */
  openInInventory(): void {
    const entry = this.current;
    if (!entry) return;
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/inventory', entry.url])
    );
    window.open(url, '_blank', 'noopener');
  }

  /** Back to the top of the belt, without reloading the brief. */
  restart(): void {
    if (!this.queue.length) return;
    this.finished = false;
    this.index = 0;
    this.markSeen();
  }

  // ── Keyboard ─────────────────────────────────────────────────────────────

  /**
   * The loop is keyboard-first, which is the whole point of it.
   *
   * Bound on the document rather than the host because focus lives inside
   * `app-url-detail` the moment the operator interacts with a card, and a
   * handler that stops working as soon as you click something is not a
   * shortcut. The guard below is what makes that safe: a screen this one embeds
   * has text inputs and checkboxes in it, and `e` must type an `e` when the
   * caret is in a field. Modifier chords are left alone entirely — those belong
   * to the browser.
   */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.loading || this.error) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (isTypingTarget(event.target)) return;

    switch (event.key) {
      case 'j':
      case 'n':
      case 'ArrowRight':
        event.preventDefault();
        this.next();
        return;
      case 'k':
      case 'p':
      case 'ArrowLeft':
        event.preventDefault();
        this.previous();
        return;
      case 'e':
        event.preventDefault();
        this.dismiss();
        return;
      case 'u':
        event.preventDefault();
        this.undo();
        return;
      case 'i':
        event.preventDefault();
        this.toggleIssuesOnly();
        return;
      case 'o':
        event.preventDefault();
        this.openInInventory();
        return;
      case 's':
        event.preventDefault();
        this.send();
        return;
      case 'Escape':
        event.preventDefault();
        this.exit();
        return;
      default:
        return;
    }
  }
}

/**
 * Whether a keypress belongs to something the operator is typing into.
 *
 * Checked by shape rather than by asking the embedded screen, because the embed
 * is free to grow another input without telling this component about it.
 */
function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  return el.isContentEditable === true;
}
